/**
 * De dónde salen las comunidades de "Explorar" (§19).
 *
 * Combina el descubrimiento de la instancia activa (`GET /api/v1/discovery`)
 * con el directorio global configurado. La agregación usa `Promise.allSettled`:
 * una fuente caída no puede tumbar la lista de las demás.
 */
import { api } from "./api.ts";
import type { MessageKey } from "../i18n.ts";
import type { CommunityCategory } from "@distop/protocol";
import { clearPendingPublicJoin, clientOrigin, connectToInstance, isPackaged, normalizeInstanceUrl, peekPendingPublicJoin, storePendingPublicJoin } from "./instance.ts";

/** Una ficha del directorio, tal como la devuelve el descubrimiento. */
export interface DirectoryCommunity {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon_url: string | null;
  banner_url: string | null;
  accent_color: string | null;
  members: number;
  visibility?: "public";
  join_policy?: "open" | "invite" | "request";
  /** Ausente en fichas de nodos anteriores a que la categoría fuese un dato. */
  category?: CommunityCategory;
  /** De qué nodo viene la ficha. Ausente = la instancia activa. */
  origin?: string;
  instance_id?: string;
  fingerprint?: string;
}

/** Un lugar al que preguntar por comunidades públicas. */
export interface DirectorySource {
  id: string;
  /** Clave de traducción, no texto: el nombre de la fuente se pinta en la vista. */
  labelKey: MessageKey;
  list: () => Promise<DirectoryCommunity[]>;
}

export interface DirectoryFailure {
  source: string;
  error: unknown;
}

export interface DirectoryListing {
  communities: DirectoryCommunity[];
  failures: DirectoryFailure[];
}

const instanceSource: DirectorySource = {
  id: "instance",
  labelKey: "explore.sourceInstance",
  list: () => api<DirectoryCommunity[]>("GET", "/api/v1/discovery"),
};

function globalSource(directoryUrl: string): DirectorySource {
  return {
    id: "global",
    labelKey: "explore.sourceGlobal",
    list: async () => {
      const response = await fetch(`${directoryUrl.replace(/\/+$/, "")}/v1/explore?limit=50`, {
        signal: AbortSignal.timeout(8_000),
        headers: { accept: "application/json" },
      });
      if (!response.ok) throw new Error(`DIRECTORY_${response.status}`);
      const body = await response.json() as { communities?: DirectoryCommunity[] };
      return Array.isArray(body.communities) ? body.communities : [];
    },
  };
}

/**
 * Las fuentes vigentes, en el orden en que se preguntan. Es una función para
 * depender de la configuración viva del nodo y del directorio global.
 */
export function directorySources(options: { localEnabled?: boolean; directoryUrl?: string } = {}): DirectorySource[] {
  const sources: DirectorySource[] = [];
  if (options.localEnabled ?? true) sources.push(instanceSource);
  if (options.directoryUrl) sources.push(globalSource(options.directoryUrl));
  return sources;
}

export type DirectoryEnterResult = "joined" | "requested" | "switching" | "unreachable" | "identity-mismatch" | "not-instance";

/** Comprueba la identidad anunciada antes de navegar o enviar una sesión. */
export async function verifyDirectoryTarget(community: DirectoryCommunity): Promise<DirectoryEnterResult | "verified"> {
  if (!community.origin) return "verified";
  const origin = normalizeInstanceUrl(community.origin);
  if (!origin) return "not-instance";
  try {
    const response = await fetch(`${origin}/api/v1/info`, { signal: AbortSignal.timeout(8_000) });
    if (!response.ok) return "not-instance";
    const info = await response.json() as { instance_id?: string; identity?: { fingerprint?: string } };
    if (community.instance_id && info.instance_id !== community.instance_id) return "identity-mismatch";
    if (community.fingerprint && info.identity?.fingerprint !== community.fingerprint) return "identity-mismatch";
    return "verified";
  } catch {
    return "unreachable";
  }
}

export async function enterDirectoryCommunity(community: DirectoryCommunity): Promise<DirectoryEnterResult> {
  const verified = await verifyDirectoryTarget(community);
  if (verified !== "verified") return verified;
  const origin = community.origin ? normalizeInstanceUrl(community.origin) : null;
  const policy = community.join_policy === "request" ? "request" : "open";
  if (!origin || origin === normalizeInstanceUrl(clientOrigin())) {
    await api("POST", `/api/v1/public-communities/${encodeURIComponent(community.id)}/${policy === "open" ? "join" : "requests"}`, {});
    return policy === "open" ? "joined" : "requested";
  }
  if (isPackaged()) {
    storePendingPublicJoin({ communityId: community.id, policy });
    const result = await connectToInstance(origin);
    return result === "ok" ? "switching" : result === "invalid" ? "not-instance" : result;
  }
  location.assign(`${origin}/?join=${encodeURIComponent(community.id)}&policy=${policy}`);
  return "switching";
}

/**
 * Termina, ya dentro del servidor, la entrada elegida en Explorar.
 *
 * El objetivo sale de `?join=` (web) o del pendiente guardado antes de cambiar
 * de instancia (app). Solo se borra cuando el servidor confirma: un fallo de red
 * o un rechazo lanzan y el objetivo sigue ahí para reintentar.
 */
export async function completePendingPublicJoin(search: string): Promise<{ community?: { id: string } } | null> {
  const params = new URLSearchParams(search);
  const pending = peekPendingPublicJoin();
  const communityId = params.get("join") || pending?.communityId;
  if (!communityId) return null;
  const policy = params.get("join") ? (params.get("policy") === "request" ? "request" : "open") : (pending?.policy ?? "open");
  const endpoint = `/api/v1/public-communities/${encodeURIComponent(communityId)}/${policy === "open" ? "join" : "requests"}`;
  const result = await api<{ community?: { id: string } }>("POST", endpoint, {});
  clearPendingPublicJoin();
  return result;
}

/**
 * Pregunta a todas las fuentes y junta lo que contesten. Los fallos no se
 * tragan: vuelven con nombre para que la vista pueda decir qué fuente falló
 * en vez de enseñar una lista misteriosamente corta (§26).
 */
export async function collectDirectory(sources: DirectorySource[]): Promise<DirectoryListing> {
  const settled = await Promise.allSettled(sources.map((source) => source.list()));
  const communities: DirectoryCommunity[] = [];
  const seen = new Set<string>();
  const failures: DirectoryFailure[] = [];
  settled.forEach((result, index) => {
    const source = sources[index]!;
    if (result.status === "fulfilled") {
      for (const community of result.value) {
        /* Por id a secas: son únicos en todo el mundo (§20). Con el nodo dentro
           de la clave, tu propia comunidad salía dos veces en cuanto la
           publicabas —una por la instancia y otra por el índice— y la segunda
           te ofrecía «Entrar» en un sitio donde ya estás. */
        const key = community.id;
        if (seen.has(key)) continue;
        seen.add(key);
        communities.push(community);
      }
    }
    else failures.push({ source: source.id, error: result.reason });
  });
  return { communities, failures };
}

/** Explorar enseña de 20 en 20: la app solo comprueba lo que va a pintar. */
export const EXPLORE_PAGE_SIZE = 20;

/**
 * ¿Contesta ese servidor ahora? `no-cors` a propósito: no hace falta leer la
 * respuesta, solo saber si llega. Con CORS normal, un servidor encendido que no
 * abre su /health a este origen parecería apagado; así solo cuenta la red.
 */
async function respondsHealth(origin: string): Promise<boolean> {
  try {
    /* Sin cookies ni Referer: el servidor sondeado no sabe quién mira Explorar.
       (`redirect: "error"` no se admite con no-cors; un 4xx/5xx opaco cuenta
       como "llega", que es lo único que se pregunta.) */
    await fetch(`${origin}/health`, { mode: "no-cors", credentials: "omit", referrerPolicy: "no-referrer", signal: AbortSignal.timeout(4_000) });
    return true;
  } catch {
    return false;
  }
}

/**
 * Deja solo las comunidades cuyo servidor contesta ahora. El directorio de Deno
 * no puede saberlo en tiempo real, así que lo comprueba la app, página a página.
 * Las de la instancia activa no se sondean: ya estás ahí. Se reconoce por su
 * `instance_id` y no solo por la dirección, porque una instancia servida en
 * localhost anuncia la de su túnel. Un servidor con varias comunidades se
 * pregunta una sola vez.
 */
export async function onlineOnly(
  list: DirectoryCommunity[],
  hereInstanceId?: string,
  probe: (origin: string) => Promise<boolean> = respondsHealth,
): Promise<DirectoryCommunity[]> {
  const here = normalizeInstanceUrl(clientOrigin());
  const checks = new Map<string, Promise<boolean>>();
  const alive = await Promise.all(
    list.map((community) => {
      const origin = community.origin ? normalizeInstanceUrl(community.origin) : null;
      if (!origin || origin === here || (hereInstanceId && community.instance_id === hereInstanceId)) return true;
      if (!checks.has(origin)) checks.set(origin, probe(origin));
      return checks.get(origin)!;
    }),
  );
  return list.filter((_, index) => alive[index]);
}
